import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { contractsApi, type ContractSub, type ContractAgreementType } from '@/lib/apiClient';
import { Button, Modal } from '@/components';
import { easternTodayYmd } from '@/modules/manpower/lib/gridDates';
import DateField from './DateField';
import {
  emptySubForm, subFormFrom, validateSubForm, subCreateBody, subPatch, withStage, canGoWithoutJob,
  STAGE_OPTIONS, WAIT_CATEGORY_ORDER, WAIT_CATEGORY_LABELS, TYPE_NAMES, type SubForm,
} from './contractsModel';

/**
 * Add a subcontract, or change / void / remove one.
 *
 * An MSA is not tied to a job (#631), so while the type is MSA the job picker's blank
 * option reads "No job" and is a real choice; for any other type a job is required. A row
 * saved with no job has to stay an MSA (the API refuses anything else), so its type is locked.
 *
 * Saving sends only the fields that changed since the form opened (`subPatch`),
 * so a colleague's edit to another field, made while this was open, survives.
 * Where the row ends up — "missing items" or "cleared" — is not chosen here: the
 * API files it from the forms received, and the screen refetches to show it.
 */
export default function SubcontractModal({ row, fixedJob, onClose }: {
  /** The subcontract being changed; omitted to add one. */
  row?: ContractSub;
  /** On a job's own tab the job is known, so there is nothing to pick. */
  fixedJob?: { id: number; label: string };
  onClose: () => void;
}) {
  const editing = !!row;
  /** An existing row with no job: an MSA, and one that has to stay an MSA. */
  const jobless = !!row && row.projectId === null;
  const qc = useQueryClient();
  // Eastern, because that is the clock the API checks "not in the future" against:
  // the browser's own date runs ahead of it for anyone east of Eastern, and would
  // offer a day the API then refuses.
  const today = easternTodayYmd();

  // Fixed for the life of the modal: what the form opened with is what a change is measured against.
  const [initial] = useState<SubForm>(() => (row ? subFormFrom(row) : emptySubForm(fixedJob ? String(fixedJob.id) : '')));
  const [form, setForm] = useState<SubForm>(initial);
  /** The type picked so far may be added without a job, so a blank job is a choice and not an omission. */
  const noJobOk = canGoWithoutJob(form.agreementType);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof SubForm>(key: K, value: SubForm[K]) => setForm((f) => ({ ...f, [key]: value }));

  const optionsQ = useQuery({
    queryKey: ['contracts', 'project-options'],
    queryFn: () => contractsApi.projectOptions(),
    staleTime: 5 * 60_000,
    enabled: !editing && !fixedJob,
  });
  // A placeholder job number (SYSTAC-, ClosedWon-, IMP-, MNT-) has no subcontracts to
  // track: those are booked to a real job.
  const jobs = (optionsQ.data ?? []).filter((o) => !o.placeholderNumber);

  const finish = () => { void qc.invalidateQueries({ queryKey: ['contracts'] }); onClose(); };
  const fail = (e: unknown) => setError((e as Error)?.message || 'That did not save. Try again.');

  const save = useMutation({
    mutationFn: () => (row
      ? contractsApi.updateSub(row.id, subPatch(initial, form))
      : contractsApi.createSub(subCreateBody(form))),
    onSuccess: finish,
    onError: fail,
  });
  const voidIt = useMutation({
    mutationFn: (voided: boolean) => contractsApi.updateSub(row!.id, { voided }),
    onSuccess: finish,
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: () => contractsApi.removeSub(row!.id),
    onSuccess: finish,
    onError: fail,
  });
  const busy = save.isPending || voidIt.isPending || remove.isPending;

  const onSave = () => {
    const problem = validateSubForm(form, today, !editing);
    if (problem) { setError(problem); return; }
    if (editing && Object.keys(subPatch(initial, form)).length === 0) { onClose(); return; }
    setError(null);
    save.mutate();
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={editing ? 'Edit subcontract' : 'Add subcontract'}
      maxWidth={720}
      closeOnBackdropClick={!busy}
      footer={(
        <>
          {row && (
            <span className="ct-modal-extra">
              {row.voidedAt ? (
                <Button variant="secondary" onClick={() => voidIt.mutate(false)} isLoading={voidIt.isPending} disabled={busy}>
                  Restore
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  disabled={busy}
                  onClick={() => {
                    if (window.confirm('Void this contract? It stays on the report, struck through, for 30 days.')) voidIt.mutate(true);
                  }}
                >
                  Void
                </Button>
              )}
              {/* A voided contract is a record and the API will not remove it: restore it first. */}
              {!row.voidedAt && (
                <Button
                  variant="danger"
                  disabled={busy}
                  onClick={() => {
                    if (window.confirm('Remove this row? Only do this for a row keyed in by mistake — a contract that was canceled should be voided, not removed.')) remove.mutate();
                  }}
                >
                  Remove
                </Button>
              )}
            </span>
          )}
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={onSave} isLoading={save.isPending} disabled={busy}>Save</Button>
        </>
      )}
    >
      <div className="ct-form">
        {row ? (
          jobless ? (
            <p className="ct-help">
              No job — an MSA isn’t tied to one. It has to stay an MSA; if it is really a subcontract or PSA,
              remove it and add it again on a job.
            </p>
          ) : (
            <p className="ct-help">
              Job {row.jobNumber}{row.projectTitle ? ` · ${row.projectTitle}` : ''}. A subcontract can’t be moved to another job —
              remove it and add it again if it was keyed to the wrong one.
            </p>
          )
        ) : fixedJob ? (
          <p className="ct-help">Job {fixedJob.label}</p>
        ) : (
          <label className="ct-field">
            <span>Job</span>
            <select value={form.projectId} onChange={(e) => set('projectId', e.target.value)}>
              <option value="">
                {optionsQ.isPending ? 'Loading jobs…' : noJobOk ? 'No job (an MSA covers more than one)' : 'Pick a job…'}
              </option>
              {jobs.map((o) => (
                <option key={o.id} value={o.id}>{o.jobNumber}{o.title ? ` · ${o.title}` : ''}</option>
              ))}
            </select>
            {optionsQ.isError && <span className="ct-help">The job list didn’t load. Close this and try again.</span>}
            {/* Said for every type, not only MSA: the form opens on Subcontract, so this is where the way out has to be. */}
            <span className="ct-help">
              {noJobOk
                ? 'An MSA isn’t tied to a job: leave this on “No job”, or pick the one job it is for. '
                : 'An MSA isn’t tied to a job — choose MSA as the type below to add one without a job. '}
              Only jobs with a real job number are listed.
            </span>
          </label>
        )}

        <div className="ct-grid2">
          <label className="ct-field">
            <span>Vendor</span>
            <input value={form.vendorName} maxLength={200} onChange={(e) => set('vendorName', e.target.value)} />
          </label>
          <label className="ct-field">
            <span>Type</span>
            <select
              value={form.agreementType}
              disabled={jobless}
              onChange={(e) => set('agreementType', e.target.value as ContractAgreementType)}
            >
              {(Object.keys(TYPE_NAMES) as ContractAgreementType[]).map((k) => (
                <option key={k} value={k}>{TYPE_NAMES[k]}</option>
              ))}
            </select>
          </label>
          <label className="ct-field">
            <span>Subcontract #</span>
            <input value={form.sageDocNo} maxLength={30} placeholder="S00731" onChange={(e) => set('sageDocNo', e.target.value)} />
            <span className="ct-help">Leave blank until it has a number.</span>
          </label>
          <label className="ct-field">
            <span>Stage</span>
            <select value={form.stage} onChange={(e) => setForm((f) => withStage(f, e.target.value as SubForm['stage'], today))}>
              {STAGE_OPTIONS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <span className="ct-help">Once the contract is executed, the row files itself from the forms below.</span>
          </label>
          {form.stage === 'waiting_on_sub' && (
            <label className="ct-field">
              <span>Waiting group</span>
              <select value={form.waitCategory} onChange={(e) => set('waitCategory', e.target.value as SubForm['waitCategory'])}>
                <option value="">No group chosen</option>
                {WAIT_CATEGORY_ORDER.map((c) => <option key={c} value={c}>{WAIT_CATEGORY_LABELS[c]}</option>)}
              </select>
            </label>
          )}
          <DateField label="Originally sent" value={form.originallySentOn} max={today} onChange={(v) => set('originallySentOn', v)} />
          {form.stage === 'on_hold' && (
            <DateField label="Hold date" value={form.holdOn} max={today} onChange={(v) => set('holdOn', v)} />
          )}
        </div>

        <div className="ew-caption">Forms received</div>
        <label className="ct-field ct-field--check">
          <input type="checkbox" checked={form.newVendor} onChange={(e) => set('newVendor', e.target.checked)} />
          <span>New vendor — also needs the new vendor form before the sub can report to site</span>
        </label>
        <div className="ct-grid2">
          <DateField label="Contract signed and executed (CC)" value={form.ccOn} max={today} onChange={(v) => set('ccOn', v)} />
          <DateField label="W9 tax ID form (W9)" value={form.w9On} max={today} onChange={(v) => set('w9On', v)} />
          <DateField label="Certificate of insurance (COI)" value={form.coiOn} max={today} onChange={(v) => set('coiOn', v)} />
          {form.newVendor && (
            <DateField label="New vendor form (NV)" value={form.nvOn} max={today} onChange={(v) => set('nvOn', v)} />
          )}
        </div>

        <label className="ct-field">
          <span>Notes</span>
          <textarea rows={3} maxLength={1000} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
        </label>

        {error && <div className="ct-error" role="alert">{error}</div>}
      </div>
    </Modal>
  );
}
